import { useState, useEffect, memo } from 'react'
import { isEqual } from 'lodash'

import { CardType } from 'types/deck'

import { store } from 'app-redux/store'
import { useAppSelector } from 'app-redux/hooks'
import { selectTokens } from 'app-redux/deck/selectors'

import CardService from 'services/card.service'

import TokenCardWrapper from 'components/deckPage/tokens/TokenCardWrapper'
import CollapsibleContentContainer from 'components/elements/CollapsibleContentContainer'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import LineLoader from 'components/elements/LineLoader'

import { modifySearchResults } from 'utils/searchModfiyResults'
import { alpha } from 'app-redux/deck/helpers/sorting'
import { sleep } from 'utils/Sleeper'

import styles from './tokenContainer.module.scss'
import PhatButton from 'components/formElements/PhatButton'

type TabType = 'tokens' | 'extras' | 'related' | 'all'

const TokenContainer = memo(() => {
  const tokenInfo = useAppSelector(selectTokens)
  const [tokenIds, setTokenIds] = useState(Object.keys(tokenInfo))

  const [allCards, setAllCards] = useState<CardType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedTab, setSelectedTab] = useState<TabType>('tokens')

  const resetState = () => {
    setLoading(false)
    setAllCards([])
  }

  const fetchTokenData = async () => {
    const tokens = Object.keys(tokenInfo)
    const cards: CardType[] = []

    let fetching = true

    if (!tokens.length) return setLoading(false)

    let request = CardService.list({
      oracleCardIds: tokens,
      includeTokens: true,
      includeArtCards: true,
      includeEmblems: true,
    })

    if (!tokens.length) return resetState()

    try {
      while (fetching) {
        const response = await request

        if (!response.next) fetching = false
        else request = CardService.list({}, response.next) // We'll get the next page on the next iteration of the while

        cards.push(...response.cards)

        const deck = Object.values(store.getState().deck.cardMap) // intentionally not included in the rerender cycle.
        const modifiedTokens = modifySearchResults(deck, cards).sort(alpha)

        setAllCards(modifiedTokens)
        setError(null)

        await sleep(1000) // wait between fetching pages, the tokens are lazy loaded anyway so nbd if they take a second longer
      }
    } catch (err: any) {
      setError(err)
    } finally {
      setLoading(false)
    }

    setLoading(false)
  }

  useEffect(() => {
    const updatedIds = Object.keys(tokenInfo)

    if (!isEqual(updatedIds, tokenIds)) return setTokenIds(updatedIds)

    // If we don't need to refetch any tokens, update our tokens with our current deck state
    // This logic will be done after fetching (aka the useEffect below) if new tokens are required
    const deck = Object.values(store.getState().deck.cardMap) // intentionally not included in the rerender cycle.
    const modifiedTokens = modifySearchResults(deck, allCards).sort(alpha)

    setAllCards(modifiedTokens)
  }, [tokenInfo])

  useEffect(() => {
    fetchTokenData()
  }, [tokenIds])

  // prettier-ignore
  const tokens = allCards.filter(card => card.types.includes('Token') || card.front?.types.includes('Token') || card.types.includes('Tolkien') || card.name === 'Morph' || card.name  === 'Manifest')
  const extras = allCards.filter(card => {
    const isEmblem = card.types.includes('Emblem')
    const isRadiation = card.name === 'Radiation'
    const isMonarch = card.name === 'Monarch' || card.name === 'The Monarch'
    const isBlessed = card.name === "City's Blessing"
    const isOnAdventure = card.name === 'On an Adventure'
    const isFortell = card.name === 'Foretell'
    const isEnergy = card.name === 'Energy Reserve'
    const isDungeon =
      card.types.includes('Dungeon') || card.front?.types.includes('Dungeon') || card.back?.types.includes('Dungeon')
    const isMeld = card.layout === 'meld'

    return (
      isEmblem || isMonarch || isBlessed || isDungeon || isMeld || isOnAdventure || isRadiation || isFortell || isEnergy
    )
  })

  const shownTokens = selectedTab === 'tokens' ? tokens : extras

  let content = (
    <div className={styles.tokens}>
      {shownTokens.map((token, key) => (
        <TokenCardWrapper
          key={`${key}-${token.id}-${token.setCode}`}
          card={token}
          generatedBy={tokenInfo[token.oracleCardId] || []}
        />
      ))}
    </div>
  )

  if (!allCards.length) content = <div>This deck has no extra cards (tokens, emblems, etc)</div>
  if (loading) content = <LineLoader />
  if (error) content = <div>An error occured when fetching your decks's extra cards</div>

  return (
    <CollapsibleContentContainer
      defaultCollapsed={!allCards.length}
      name="Deck extras"
      nameExtras={`(${tokens.length + extras.length})`}
      className={styles.parent}>
      <div className={styles.container}>
        <h2>Deck Tokens & Extras ({tokens.length + extras.length}) </h2>

        <div className={styles.controls}>
          <div className={styles.left}>
            <PhatButton
              className={selectedTab === 'tokens' ? styles.archidektOrange : ''}
              onClick={() => setSelectedTab('tokens')}>
              Tokens ({tokens.length})
            </PhatButton>
            <PhatButton
              className={selectedTab === 'extras' ? styles.archidektOrange : ''}
              onClick={() => setSelectedTab('extras')}>
              Extras ({extras.length})
            </PhatButton>
          </div>

          <ArchidektDropdown
            menuClassName={styles.tooltip}
            customOptionsTop={
              <div className={styles.tooltipBlurb}>
                None of the tokens or extras below are actually "in your deck". If you want a specific token edition, or
                to manage them yourself, you can add them to the deck from here and manage them as any other card.
              </div>
            }>
            <PhatButton icon="question" />
          </ArchidektDropdown>
        </div>

        <div className={styles.tokensBox}>{content}</div>
      </div>
    </CollapsibleContentContainer>
  )
})

export default TokenContainer
